/**
 * The runtime-config contract shared by every browser app.
 *
 * A deployed container writes a small config script that sets
 * `window.__QUOVY_CONFIG__` before the bundle runs (apps/web via a container
 * `/config.js`; the operator console via the served shell — see
 * `apps/control-plane/src/routes/console.ts`). Reading it here — rather than
 * baking `VITE_*` values into the bundle — is what lets one built image serve
 * every environment. Each app derives its own typed exports from `pick`.
 */

export type RuntimeConfig = {
  apiUrl?: string;
  tenantAppDomain?: string;
  tenantApiDomain?: string;
  sentryDsn?: string;
  sentryEnvironment?: string;
  stackName?: string;
};

declare global {
  interface Window {
    __QUOVY_CONFIG__?: RuntimeConfig;
  }
}

/**
 * Read the injected config once. The container writes it before the bundle is
 * fetched, so it is present by the time any module imports this.
 */
export function readRuntimeConfig(): RuntimeConfig {
  return typeof window === "undefined" ? {} : (window.__QUOVY_CONFIG__ ?? {});
}

/**
 * Precedence: a real runtime value wins, otherwise fall back to the build value.
 *
 * A blank runtime value counts as ABSENT — the container's entrypoint templates
 * every key whether or not the operator set it, so an unset variable arrives as
 * `""`; honoring that would resolve, say, an API URL to the empty string and
 * send requests to the page's own origin.
 */
export function pick(
  fromRuntime: string | undefined,
  fromBuild: string | undefined,
): string | undefined {
  const trimmed = fromRuntime?.trim();
  if (trimmed) return trimmed;
  return fromBuild;
}
